import { lazy, type ReactElement, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { ChatProtectedRoute, ProtectedRoute } from '@/components/auth/protected-route';
import { TelegramOnlyScreen } from '@/components/auth/TelegramOnlyScreen';
import { AppShell } from '@/components/layout/AppShell';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthBootstrap } from '@/hooks/useAuth';
import { useI18n } from '@/i18n';
import { isInsideTelegramWebApp } from '@/lib/telegram-env';
import { useTelegram } from '@/providers/TelegramProvider';

const AnalyticsPage = lazy(() =>
  import('@/pages/AnalyticsPage').then((module) => ({ default: module.AnalyticsPage })),
);
const ChatDetailPage = lazy(() =>
  import('@/pages/ChatDetailPage').then((module) => ({ default: module.ChatDetailPage })),
);
const ChatGreetingsPage = lazy(() =>
  import('@/pages/ChatGreetingsPage').then((module) => ({ default: module.ChatGreetingsPage })),
);
const ChatMembersPage = lazy(() =>
  import('@/pages/ChatMembersPage').then((module) => ({ default: module.ChatMembersPage })),
);
const ChatsPage = lazy(() =>
  import('@/pages/ChatsPage').then((module) => ({ default: module.ChatsPage })),
);
const DashboardPage = lazy(() =>
  import('@/pages/DashboardPage').then((module) => ({ default: module.DashboardPage })),
);
const AuditRedirectPage = lazy(() =>
  import('@/pages/LogsPage').then((module) => ({ default: module.AuditRedirectPage })),
);
const LogsPage = lazy(() =>
  import('@/pages/LogsPage').then((module) => ({ default: module.LogsPage })),
);
const MessageTemplateFormPage = lazy(() =>
  import('@/pages/MessageTemplateFormPage').then((module) => ({
    default: module.MessageTemplateFormPage,
  })),
);
const MessageTemplatesPage = lazy(() =>
  import('@/pages/MessageTemplatesPage').then((module) => ({
    default: module.MessageTemplatesPage,
  })),
);
const ReminderCreateGroupsPage = lazy(() =>
  import('@/pages/ReminderCreateGroupsPage').then((module) => ({
    default: module.ReminderCreateGroupsPage,
  })),
);
const ReminderCreatePage = lazy(() =>
  import('@/pages/ReminderCreatePage').then((module) => ({ default: module.ReminderCreatePage })),
);
const ReminderCreatePreviewPage = lazy(() =>
  import('@/pages/ReminderCreatePreviewPage').then((module) => ({
    default: module.ReminderCreatePreviewPage,
  })),
);
const ReminderDetailPage = lazy(() =>
  import('@/pages/ReminderDetailPage').then((module) => ({ default: module.ReminderDetailPage })),
);
const RemindersPage = lazy(() =>
  import('@/pages/RemindersPage').then((module) => ({ default: module.RemindersPage })),
);
const SettingsPage = lazy(() =>
  import('@/pages/SettingsPage').then((module) => ({ default: module.SettingsPage })),
);
const TelegramApiSetupPage = lazy(() =>
  import('@/pages/settings/TelegramApiSetupPage').then((module) => ({
    default: module.TelegramApiSetupPage,
  })),
);

function AuthGate({ children }: { children: ReactElement }): ReactElement {
  const { t } = useI18n();
  const { isReady, initDataMissing } = useTelegram();
  const { isLoading, isAuthenticated, error, isAccessDenied } = useAuthBootstrap();

  if (!import.meta.env.DEV && isReady && !isInsideTelegramWebApp()) {
    return <TelegramOnlyScreen />;
  }

  if (!import.meta.env.DEV && isReady && isInsideTelegramWebApp() && initDataMissing) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 text-center">
        <div>
          <h1 className="mb-2 text-lg font-semibold">{t('auth.authError')}</h1>
          <p className="text-sm text-muted">{t('auth.telegramInitMissing')}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-[var(--app-height)] flex-col gap-4 overflow-hidden p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (error && !isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4 text-center">
        <div>
          <h1 className="mb-2 text-lg font-semibold">
            {isAccessDenied ? t('auth.accessDenied') : t('auth.authError')}
          </h1>
          <p className="text-sm text-muted">{error.message}</p>
          {import.meta.env.DEV ? (
            <p className="mt-4 text-xs text-muted">{t('auth.devHint')}</p>
          ) : null}
        </div>
      </div>
    );
  }

  return children;
}

function RetiredBirthdaysRedirect(): ReactElement {
  const { search, hash } = useLocation();
  return (
    <Navigate
      to={{ pathname: '/reminders', search, hash }}
      replace
    />
  );
}

export function App(): ReactElement {
  return (
    <AuthGate>
      <AppShell>
        <Suspense
          fallback={
            <div className="space-y-4 p-4">
              <Skeleton className="h-8 w-40" />
              <Skeleton className="h-32 w-full" />
            </div>
          }
        >
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute permission="chats:read">
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute permission="chats:read">
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders"
              element={
                <ProtectedRoute permission="reminders:read">
                  <RemindersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders/new"
              element={
                <ProtectedRoute permission="reminders:write">
                  <ReminderCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders/new/groups"
              element={
                <ProtectedRoute permission="reminders:write">
                  <ReminderCreateGroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders/new/preview"
              element={
                <ProtectedRoute permission="reminders:write">
                  <ReminderCreatePreviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders/:id"
              element={
                <ProtectedRoute permission="reminders:read">
                  <ReminderDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders/:id/edit"
              element={
                <ProtectedRoute permission="reminders:write">
                  <ReminderCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders/:id/edit/groups"
              element={
                <ProtectedRoute permission="reminders:write">
                  <ReminderCreateGroupsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reminders/:id/edit/preview"
              element={
                <ProtectedRoute permission="reminders:write">
                  <ReminderCreatePreviewPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chats"
              element={
                <ProtectedRoute permission="chats:read">
                  <ChatsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chats/:id"
              element={
                <ChatProtectedRoute permission="chats:read">
                  <ChatDetailPage />
                </ChatProtectedRoute>
              }
            />
            <Route
              path="/chats/:id/greetings"
              element={
                <ChatProtectedRoute permission="settings:write">
                  <ChatGreetingsPage />
                </ChatProtectedRoute>
              }
            />
            <Route
              path="/chats/:id/members"
              element={
                <ChatProtectedRoute permission="members:read">
                  <ChatMembersPage />
                </ChatProtectedRoute>
              }
            />
            <Route
              path="/audit"
              element={<AuditRedirectPage />}
            />
            <Route
              path="/birthdays/*"
              element={<RetiredBirthdaysRedirect />}
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute permission="reminders:read">
                  <MessageTemplatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/new"
              element={
                <ProtectedRoute permission="reminders:write">
                  <MessageTemplateFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/:id/edit"
              element={
                <ProtectedRoute permission="reminders:write">
                  <MessageTemplateFormPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logs"
              element={
                <ProtectedRoute permission="reminders:read">
                  <LogsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute permission="settings:read">
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings/telegram-api"
              element={
                <ProtectedRoute permission="settings:write">
                  <TelegramApiSetupPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="*"
              element={
                <Navigate
                  to="/"
                  replace
                />
              }
            />
          </Routes>
        </Suspense>
      </AppShell>
    </AuthGate>
  );
}
